import React, { useState } from 'react';
import { useAccessibilityMode } from '../../contexts/AccessibilityModeContext';
import { Settings2, Check } from 'lucide-react';

/**
 * Mode Switcher — toggle Standard / Senior / Easy modes.
 * Stored globally via AccessibilityModeContext.
 */
const MODES = [
  { key: 'standard', label: 'Standard', desc: 'Default CaraLoom experience' },
  { key: 'senior', label: 'Senior', desc: 'Larger text, simpler layout, voice-friendly' },
  { key: 'easy', label: 'Easy', desc: 'One question at a time, calm pacing' },
];

export const ModeSwitcher = ({ compact = false }) => {
  const { mode, setMode } = useAccessibilityMode();
  const [open, setOpen] = useState(false);
  const current = MODES.find((m) => m.key === mode) || MODES[0];

  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={() => setOpen(!open)}
          data-testid="mode-switcher-compact"
          aria-label="Change accessibility mode"
          className="p-2.5 rounded-full bg-white/60 dark:bg-white/5 border border-cn-border/60 hover:bg-white text-cn-text-muted hover:text-cn-text transition-colors"
        >
          <Settings2 className="w-4 h-4" />
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <div className="absolute right-0 mt-2 w-72 z-40 rounded-2xl bg-white dark:bg-[#0F172A] border border-cn-border/60 shadow-xl overflow-hidden">
              <div className="p-3 border-b border-cn-border/60">
                <p className="cn-eyebrow">View mode</p>
                <p className="text-sm font-bold text-cn-text dark:text-white mt-0.5">How would you like CaraLoom to feel?</p>
              </div>
              <div className="p-2">
                {MODES.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => { setMode(m.key); setOpen(false); }}
                    data-testid={`mode-${m.key}`}
                    className={`w-full flex items-start gap-3 p-3 rounded-xl text-left transition-colors ${
                      mode === m.key ? 'bg-cn-primary/10' : 'hover:bg-cn-bg dark:hover:bg-white/5'
                    }`}
                  >
                    <div className={`w-5 h-5 mt-0.5 rounded-full flex items-center justify-center ${mode === m.key ? 'bg-cn-primary text-white' : 'border-2 border-cn-border'}`}>
                      {mode === m.key && <Check className="w-3 h-3" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-cn-text dark:text-white">{m.label}</p>
                      <p className="text-xs text-cn-text-muted">{m.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-2 p-1 bg-cn-bg rounded-2xl border border-cn-border/60">
      {MODES.map((m) => (
        <button
          key={m.key}
          onClick={() => setMode(m.key)}
          data-testid={`mode-${m.key}`}
          className={`text-xs font-semibold py-2 rounded-xl transition-all ${
            mode === m.key ? 'bg-white shadow-sm text-cn-primary' : 'text-cn-text-muted hover:text-cn-text'
          }`}
        >{m.label}</button>
      ))}
    </div>
  );
};

export default ModeSwitcher;
